import { Injectable, Logger } from '@nestjs/common';
import { InjectRepository } from '@nestjs/typeorm';
import { Repository } from 'typeorm';
import { SupabaseService } from '../supabase/supabase.service';
import { UsersEntity, SignupDto, SigninDto } from '@crm/dto';
import { ApiResponseInterface, ApiResponseStatus, UserRole, UserStatus } from '@crm/interface';
import { API_RESPONSE, toMiniUser } from '@crm/utils';

@Injectable()
export class AuthService {
  private readonly logger = new Logger(AuthService.name);

  constructor(
    @InjectRepository(UsersEntity)
    private readonly usersRepo: Repository<UsersEntity>,
    private readonly supabase: SupabaseService,
  ) {}

  /**
   * Flow:
   * 1. Check if user exists in users table using TypeORM
   * 2. If exists, return error
   * 3. Sign up user via Supabase (confirmation email sent automatically)
   * 4. Save user in MySQL users table
   * 5. Return success
   */
  async signup(dto: SignupDto): Promise<ApiResponseInterface> {
    try {
      const existingUser = await this.usersRepo.findOne({
        where: { email: dto.email },
      });

      if (existingUser) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.AUTH.USER_EXISTS.CODE,
          message: API_RESPONSE.ERROR.AUTH.USER_EXISTS.MESSAGE,
          data: null,
        };
      }

      const { data, error } = await this.supabase.client.auth.signUp({
        email: dto.email,
        password: dto.password,
        options: {
          emailRedirectTo: dto.redirectUrl
        }
      }); 

      if (error) {
        this.logger.error(error.message, error);
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.AUTH.SIGN_UP.CODE,
          message: API_RESPONSE.ERROR.AUTH.SIGN_UP.MESSAGE,
          data: error,
        };
      }

      const newUser = this.usersRepo.create({
        id: data.user.id, 
        email: dto.email,
        role: UserRole.MANAGER,
        status: UserStatus.PENDING
      });
      await this.usersRepo.save(newUser);

      newUser.created_by = newUser;
      newUser.updated_by = newUser;
      await this.usersRepo.save(newUser);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.AUTH.SIGN_UP.CODE,
        message: API_RESPONSE.SUCCESS.AUTH.SIGN_UP.MESSAGE,
        data: {
          user: {
            ...newUser,
            created_by: toMiniUser(newUser.created_by),
            updated_by: toMiniUser(newUser.updated_by),
          }
        },
      };

    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.AUTH.SIGN_UP.CODE,
        message: API_RESPONSE.ERROR.AUTH.SIGN_UP.MESSAGE,
        data: error,
      };
    }
  }

  /**
   * flow:
   * 1. check if user exist in users table
   * 2. if !exist return error else continue
   * 3. signin user using supabase auth signin
   * 4. if error return error else continue
   * 5. get user data from user db
   * 6. check if first_name, last_name, and position are empty
   * 7. if true return user data, session details, and redirect url to /account-setup
   * 8. if false return user data and session details
  */
  async signin(dto: SigninDto): Promise<ApiResponseInterface> {
    try {
      const user = await this.usersRepo.findOne({
        where: { email: dto.email },
      });

      if (!user) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.AUTH.USER_NOT_FOUND.CODE,
          message: API_RESPONSE.ERROR.AUTH.USER_NOT_FOUND.MESSAGE,
          data: null,
        };
      }
      
      const { data, error } = await this.supabase.client.auth.signInWithPassword({
        email: dto.email,
        password: dto.password
      });

      if (error) {
        this.logger.error(error.message, error);
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.AUTH.SIGN_IN.CODE,
          message: API_RESPONSE.ERROR.AUTH.SIGN_IN.MESSAGE,
          data: error,
        };
      }

      const isProfileIncomplete =
        !user.first_name || !user.last_name || !user.position;

      const responseData: any = {
        user: user,
        session: data.session,
      };

      if (isProfileIncomplete) {
        responseData.redirectUrl = '/account-setup';
      }

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.AUTH.SIGN_IN.CODE,
        message: API_RESPONSE.SUCCESS.AUTH.SIGN_IN.MESSAGE,
        data: responseData,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.AUTH.SIGN_IN.CODE,
        message: API_RESPONSE.ERROR.AUTH.SIGN_IN.MESSAGE,
        data: error,
      };
    }
  }

  /**
   * flow
   * 1. sign in user with google using supabase oath provider google
   * 2. if error return error else continue
   * 3. check if user exist in user db
   * 4. if !exist create user with the id of supabase auth id then go to step 8
   * 5. else check if first_name, last_name, and position are empty
   * 6. if true go to step 8
   * 7. else go to step 9
   * 8. return user data, session details, and redirect url to /account-setup
   * 9. return user data and session details
   * */ 
  async signinWithOAth(requestUrl: string): Promise<ApiResponseInterface> {
    try {
      const { data, error } = await this.supabase.client.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${requestUrl}/sign-in`
        }
      });   

      if (error) {
        this.logger.error(error.message, error.stack);
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.AUTH.SIGN_IN.CODE,
          message: API_RESPONSE.ERROR.AUTH.SIGN_IN.MESSAGE,
          data: error
        }
      }

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.AUTH.SIGN_IN.CODE,
        message: API_RESPONSE.SUCCESS.AUTH.SIGN_IN.MESSAGE,
        data: data
      }
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.AUTH.SIGN_IN.CODE,
        message: API_RESPONSE.ERROR.AUTH.SIGN_IN.MESSAGE,
        data: error,
      };
    }
  }

  async signout(): Promise<ApiResponseInterface> {
    try {
      const { error } = await this.supabase.client.auth.signOut()

      if (error) {
        this.logger.error(error.message, error.stack);
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.AUTH.SIGN_OUT.CODE,
          message: API_RESPONSE.ERROR.AUTH.SIGN_OUT.MESSAGE,
          data: error
        }
      }

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.AUTH.SIGN_OUT.CODE,
        message: API_RESPONSE.SUCCESS.AUTH.SIGN_OUT.MESSAGE,
        data: true
      }
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.AUTH.SIGN_OUT.CODE,
        message: API_RESPONSE.ERROR.AUTH.SIGN_OUT.MESSAGE,
        data: error,
      };
    }
  }

  /**
   * POST /auth/forgot-password/request
   * 1. Check if user exists in MySQL
   * 2. If not, return error
   * 3. Send password reset email via Supabase
   */
  async forgotPasswordRequest(email: string, redirectTo: string): Promise<ApiResponseInterface> {
    try {
      const user = await this.usersRepo.findOne({ where: { email } });
      if (!user) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.AUTH.USER_NOT_FOUND.CODE,
          message: API_RESPONSE.ERROR.AUTH.USER_NOT_FOUND.MESSAGE,
          data: null,
        };
      }

      const { error } = await this.supabase.client.auth.resetPasswordForEmail(email, {
        redirectTo,
      });

      if (error) {
        this.logger.error(error.message, error);
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.AUTH.RESET_PASSWORD.CODE,
          message: API_RESPONSE.ERROR.AUTH.RESET_PASSWORD.MESSAGE,
          data: error,
        };
      }

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.AUTH.RESET_PASSWORD.CODE,
        message: 'Password reset email sent. Please check your inbox.',
        data: null,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.AUTH.RESET_PASSWORD.CODE,
        message: API_RESPONSE.ERROR.AUTH.RESET_PASSWORD.MESSAGE,
        data: error,
      };
    }
  }

  /**
   * POST /auth/forgot-password/reset
   * 1. Verify the access token via Supabase
   * 2. Update the password using admin client
   */
  async forgotPasswordReset(accessToken: string, newPassword: string): Promise<ApiResponseInterface> {
    try {
      const { data: userData, error: userError } = await this.supabase.client.auth.getUser(accessToken);

      if (userError || !userData?.user) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.AUTH.RESET_PASSWORD.CODE,
          message: 'Invalid or expired reset token.',
          data: null,
        };
      }

      const { error } = await this.supabase.clientAdmin.auth.admin.updateUserById(
        userData.user.id,
        { password: newPassword },
      );

      if (error) {
        this.logger.error(error.message, error);
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.AUTH.RESET_PASSWORD.CODE,
          message: API_RESPONSE.ERROR.AUTH.RESET_PASSWORD.MESSAGE,
          data: error,
        };
      }

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.AUTH.RESET_PASSWORD.CODE,
        message: 'Password updated successfully.',
        data: null,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.AUTH.RESET_PASSWORD.CODE,
        message: API_RESPONSE.ERROR.AUTH.RESET_PASSWORD.MESSAGE,
        data: error,
      };
    }
  }

  /**
   * POST /auth/resend-confirmation
   * Resend email confirmation for unconfirmed users.
   */
  async resendConfirmation(email: string): Promise<ApiResponseInterface> {
    try {
      const user = await this.usersRepo.findOne({ where: { email } });
      if (!user) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.AUTH.USER_NOT_FOUND.CODE,
          message: API_RESPONSE.ERROR.AUTH.USER_NOT_FOUND.MESSAGE,
          data: null,
        };
      }

      const { error } = await this.supabase.client.auth.resend({
        type: 'signup',
        email,
      });

      if (error) {
        this.logger.error(error.message, error);
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.AUTH.RESEND_CONFIRMATION_LINK.CODE,
          message: API_RESPONSE.ERROR.AUTH.RESEND_CONFIRMATION_LINK.MESSAGE,
          data: error,
        };
      }

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.AUTH.RESEND_CONFIRMATION_LINK.CODE,
        message: API_RESPONSE.SUCCESS.AUTH.RESEND_CONFIRMATION_LINK.MESSAGE,
        data: null,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.AUTH.RESEND_CONFIRMATION_LINK.CODE,
        message: API_RESPONSE.ERROR.AUTH.RESEND_CONFIRMATION_LINK.MESSAGE,
        data: error,
      };
    }
  }
}